/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: TenantSelectSimple.tsx
 🗓️ Created: 2025-01-19
 🧩 Description:
    Generic tenant selection component with search functionality,
    supporting both string and numeric IDs for flexible usage across modules.

 🔧 Notes:
    - Implements searchable dropdown with icon and visual feedback
    - Supports keyboard navigation (arrow keys, enter, escape)
    - Includes optional "Global Access" option for null values
    - Optimized for performance with filtered results
    - Integrates with form validation systems
    - Can be used in API Keys, User assignments, and other modules

 🔁 Last Updated: 2025-01-19 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronDown, Search, Building2 } from 'lucide-react';

interface Tenant {
  id: string | number;
  name: string;
  slug: string;
}

interface TenantSelectSimpleProps {
  value: string | number | null | undefined;
  onChange: (value: string | number | null) => void;
  tenants: Tenant[];
  className?: string;
  placeholder?: string;
  globalAccessLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  showGlobalAccess?: boolean;
  searchPlaceholder?: string;
  noResultsText?: string;
  noDataText?: string;
}

export const TenantSelectSimple: React.FC<TenantSelectSimpleProps> = ({
  value,
  onChange,
  tenants,
  className = '',
  placeholder = 'Select tenant...',
  globalAccessLabel = 'Global Access',
  disabled = false,
  loading = false,
  error = false,
  showGlobalAccess = true,
  searchPlaceholder = 'Search tenants...',
  noResultsText = 'No results found',
  noDataText = 'No tenants available'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const comboboxRef = useRef<HTMLDivElement>(null);

  // Close combobox when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (comboboxRef.current && !comboboxRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter tenants based on search query
  const filteredTenants = useMemo(() => {
    if (!searchQuery.trim()) {
      return tenants;
    }
    
    const query = searchQuery.toLowerCase();
    return tenants.filter(tenant => 
      tenant.name.toLowerCase().includes(query) ||
      tenant.slug.toLowerCase().includes(query)
    );
  }, [tenants, searchQuery]);

  // Get selected tenant display label
  const selectedTenantLabel = useMemo(() => {
    if (!value) return null;
    const tenant = tenants.find(t => String(t.id) === String(value));
    return tenant ? `${tenant.name} (${tenant.slug})` : null;
  }, [value, tenants]);

  // Handle selection
  const handleSelect = (tenantValue: string | number | null) => {
    onChange(tenantValue);
    setIsOpen(false);
    setSearchQuery('');
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case 'Escape':
        setIsOpen(false);
        setSearchQuery('');
        break;
      case 'Enter':
        if (!isOpen) {
          e.preventDefault();
          setIsOpen(true);
        }
        break;
    }
  };

  return (
    <div className={`relative ${className}`} ref={comboboxRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled || loading}
        className={`
          w-full px-3 py-2 text-sm border rounded bg-background text-foreground 
          focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent
          flex items-center justify-between gap-2
          ${disabled || loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}
          ${isOpen ? 'ring-1 ring-primary border-transparent' : 'border-border'}
          ${error ? 'border-red-500' : ''}
        `}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="truncate text-left">
            {loading 
              ? 'Loading...' 
              : selectedTenantLabel || (showGlobalAccess ? globalAccessLabel : placeholder)
            }
          </span>
        </div>
        <ChevronDown 
          className={`h-4 w-4 text-muted-foreground transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {/* Global Access Option */}
            {showGlobalAccess && (
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className={`
                  w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors
                  flex items-center gap-2
                  ${!value ? 'bg-primary/10 text-primary' : 'text-foreground'}
                `}
              >
                <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="truncate">{globalAccessLabel}</span>
              </button>
            )}

            {/* Tenant Options */}
            {filteredTenants.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                {searchQuery ? noResultsText : noDataText}
              </div>
            ) : (
              filteredTenants.map((tenant) => (
                <button
                  key={tenant.id}
                  type="button"
                  onClick={() => handleSelect(tenant.id)}
                  className={`
                    w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors
                    flex items-center gap-2
                    ${String(value) === String(tenant.id) ? 'bg-primary/10 text-primary' : 'text-foreground'}
                  `}
                >
                  <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{tenant.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{tenant.slug}</div>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer with count */}
          {tenants.length > 0 && (
            <div className="px-3 py-2 border-t border-border bg-muted/30">
              <p className="text-xs text-muted-foreground">
                {filteredTenants.length} of {tenants.length} {tenants.length === 1 ? 'tenant' : 'tenants'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TenantSelectSimple;

